import { get, isArray, uniq } from 'lodash'

import {
  useSchemeProductConfigListQuery,
  ProductType,
} from '~/gql/generated/graphql'
import { useCallback } from 'react'
import { isNotNullOrUndefined } from '~/utils/types'

export type OtherValuesProps = {
  getOtherValues: (fieldName: string | string[]) => string | undefined
}
const useCompareOtherSchemeProducts = ({
  schemeId,
  productType,
}: {
  schemeId: string
  productType: ProductType
}): OtherValuesProps => {
  // Fetch all the pricing configs for this scheme once
  const { data, loading } = useSchemeProductConfigListQuery({
    variables: { schemeId },
    pollInterval: 0,
    fetchPolicy: 'cache-first',
  })

  // Return a function that concatinates all the unique values for a given field in the scheme
  const getOtherValues = useCallback(
    (fieldName: string | string[]) => {
      if (loading) {
        return '...'
      }
      const path = isArray(fieldName) ? fieldName.join('.') : fieldName
      const options =
        uniq(
          data?.all_scheme_products.nodes
            .filter(p => productType === p.product_type)
            .map(item => {
              return get(item, path)
            })
            .map(item => {
              if (item?.__typename === 'FormattedAmount') {
                return item.formatted_amount
              }
              return item
            }),
        ) ?? []

      const nonNullOptions = options.filter(isNotNullOrUndefined)
      if (nonNullOptions.length === 0) {
        return undefined
      }

      // If there are nulls, this field is sometimes null
      if (options.length > nonNullOptions.length) {
        return `Sometimes: ${nonNullOptions.join(' or ')}`
      }

      // No nulls so these are the only other options
      if (options.length > 0) {
        return `Generally: ${nonNullOptions.join(' or ')}`
      }
    },
    [data?.all_scheme_products.nodes, productType, loading],
  )

  return { getOtherValues }
}

export default useCompareOtherSchemeProducts
